import React from "react";
import { NavLink, useMatch, useSearchParams } from "react-router-dom";
import {
  Home, BookOpen, Monitor, ScrollText, BarChart2,
  Users, Upload, X, CheckSquare, FileDown, Cpu, Library, UserRound, Smartphone, HeartPulse,ShieldAlert, BellRing, TrendingUp
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import { MOCK_CLIENTS } from "../../lib/mockData";

const pendingClientCount = MOCK_CLIENTS.filter((c) => c.pending).length;

export function Sidebar({ open, onClose }) {
  const { role } = useAuth();
  const onDeviceDetail = useMatch("/devices/:serial");
  const [searchParams] = useSearchParams();
  const detailTab = searchParams.get("tab") || "applog";
  // The tabs that keep the Device Detail nav entry visible. A tab missing from
  // this list makes the entry vanish the moment the user opens it.
  const DETAIL_TABS_WITH_NAV = ["applog", "audit", "otalog"];
  const showDeviceDetail = !!onDeviceDetail && DETAIL_TABS_WITH_NAV.includes(detailTab);

  const isAdmin = role === "FleetAdmin";
  const isRM = role === "ReleaseManager";
  const isSupport = role === "SupportEngineer";
  // Read-only roles (003 Role Gate): can view fleet data, but never download.
  const isReadOnly = role === "Nurse" || role === "Patient";

  return (
    <aside className={cn(
      "flex flex-col w-56 bg-blue-50",
      "fixed inset-y-0 left-0 z-30 transition-transform duration-200 ease-in-out lg:static lg:z-auto lg:transition-none",
      open ? "translate-x-0" : "-translate-x-full lg:hidden"
    )}>
      {/* Mobile close button */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-3 right-3 p-1.5 rounded-md text-blue-400 hover:text-blue-700 hover:bg-blue-100 transition-colors"
        aria-label="Close sidebar"
      >
        <X className="h-4 w-4" />
      </button>
      {/* Dark blue top strip matching header height */}
      <div className="h-16 bg-[#08148C] shrink-0" />
      <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-5 border-r border-blue-200">

        {/* NAVIGATION */}
        <div>
          <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-500 bg-blue-100 rounded py-1">Navigation</p>
          <SideItem to="/" icon={Home} label="Home" end />
        </div>

        {/* INSTALL BASELINE */}
        {(isAdmin || isRM) && (
          <div>
            <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-500 bg-blue-100 rounded py-1">Install Baseline</p>
            <SideItem to="/firmware" icon={BookOpen} label="View Baseline" end />
            <SideItem to="/firmware/upload" icon={Upload} label="New Baseline" />
          </div>
        )}

        {/* FLEET */}
        <div>
          <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-500 bg-blue-100 rounded py-1">Fleet</p>
          {(isAdmin || isSupport || isReadOnly) && (
            <SideItem to="/devices" icon={Monitor} label="Fleet Overview" end />
          )}
          {(isAdmin || isSupport || isReadOnly) && showDeviceDetail && (
            <SideItem to={`/devices/${onDeviceDetail.params.serial}?tab=${detailTab}`} icon={Cpu} label="Device Detail" />
          )}
          {(isAdmin || isRM) && (
            <SideItem to="/admin/audit" icon={ScrollText} label="Activity Trace" />
          )}
          {(isAdmin || isSupport) && (
            <SideItem to="/admin/unusual-activity" icon={ShieldAlert} label="Unusual Activity" />
          )}
        </div>

        {/* REPORTS */}
        <div>
          <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-500 bg-blue-100 rounded py-1">Reports</p>
          <SideItem to="/rollouts" icon={BarChart2} label="Rollout Summary" />
          <SideItem to="/telemetry" icon={CheckSquare} label="Compliance Table" end />
          <SideItem to="/telemetry/export" icon={FileDown} label="Export" end />
        </div>

        {/* ACTIVITY SESSIONS */}
        <div>
          <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-500 bg-blue-100 rounded py-1">Activity Sessions</p>
          {(isAdmin || isSupport) && (
            <SideItem to="/sessions/clients" icon={UserRound} label="Client" badge={pendingClientCount || null} badgeColor="amber" />
          )}
          {(isAdmin || isSupport) && (
            <SideItem to="/sessions/devices" icon={Smartphone} label="Device" />
          )}
          {/* FleetAdmin alone, unlike its two siblings: the analytics routes are
              ADMIN in role_matrix, and the refresh runs a billed Athena query. */}
          {isAdmin && (
            <SideItem to="/sessions/analytics" icon={TrendingUp} label="Analytics" />
          )}
        </div>

        {/* ADMIN */}
        {(isAdmin || isSupport) && (
          <div>
            <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-500 bg-blue-100 rounded py-1">Administration</p>
            {isAdmin && <SideItem to="/admin/users" icon={Users} label="User Management" />}
            {isAdmin && <SideItem to="/admin/notifications" icon={BellRing} label="Send Notification" />}
            {isAdmin && <SideItem to="/admin/device-catalog" icon={Library} label="Device Catalog" />}
            <SideItem to="/admin/system-health" icon={HeartPulse} label="System Health" />
          </div>
        )}

      </nav>
    </aside>
  );
}

// Active wins over "future"; both fall back to the ordinary link styling.
function sideItemStateClass(isActive, future) {
  if (isActive) return "bg-blue-200 text-[#08148C] font-semibold border-l-2 border-blue-600 pl-[6px]";
  if (future) return "text-blue-300 hover:text-blue-400 hover:bg-blue-100";
  return "text-blue-800 hover:text-[#08148C] hover:bg-blue-100 font-medium";
}

function SideItem({ to, icon: Icon, label, end, badge, badgeColor, iconColor, future }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors mb-0.5 group",
          sideItemStateClass(isActive, future)
        )
      }
    >
      <Icon className={cn("h-4 w-4 shrink-0", iconColor)} />
      <span className="flex-1 truncate">{label}</span>
      {badge != null && (
        <span className={cn(
          "text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center",
          badgeColor === "teal" ? "bg-teal-100 text-[#08148C]" : "bg-amber-100 text-amber-700"
        )}>
          {badge}
        </span>
      )}
      {future && (
        <span className="text-[10px] font-semibold bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">INC2</span>
      )}
    </NavLink>
  );
}
